import type { TeamCapacitySummary } from '@/domains/calendar/hooks/useTeamCapacities';
import type { Job } from '@/domains/jobs/components/JobCard';
import { Badge } from '@/shared/ui/badge';
import { addDays, format } from 'date-fns';
import { MapPin } from 'lucide-react';
import { useMemo, useState } from 'react';
import Map, { Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { CalendarTeam } from './CalendarView';
import { MapTeamUtilizationPanel } from './MapTeamUtilizationPanel';

const MAPBOX_TOKEN =
  import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || import.meta.env.VITE_MAPBOX_TOKEN || '';

interface PlannerMapViewProps {
  jobs: Job[];
  onJobClick?: (jobId: string) => void;
  teams?: CalendarTeam[];
  summariesByTeam?: Map<string, TeamCapacitySummary>;
  /** Left edge of the visible day window (inclusive), from the toolbar. */
  rangeStart: Date;
  /** Number of days visible — the 1–7 day range selector. */
  rangeDays: number;
}

function parseCoords(coordinates?: string): { lat: number; lng: number } | null {
  if (!coordinates) return null;
  const parts = coordinates.split(',').map((s) => parseFloat(s.trim()));
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
  if (Math.abs(parts[0]) <= 90) return { lat: parts[0], lng: parts[1] };
  return { lat: parts[1], lng: parts[0] };
}

/** Normalise a scheduled date/datetime string to a yyyy-MM-dd day key. */
function toDayKey(value?: string): string | null {
  if (!value) return null;
  // scheduledDate arrives as either 'yyyy-MM-dd' or 'yyyy-MM-ddThh:mm:ss';
  // the first 10 chars are the calendar day in both cases.
  return value.slice(0, 10);
}

/**
 * A job is in the window if any of its scheduled days overlaps
 * [rangeStart, rangeStart + rangeDays). Uses the same day-key comparison as the
 * grid so a multi-day job shows whenever part of it falls in range.
 */
function isInDayWindow(job: Job, dayKeys: Set<string>): boolean {
  const start = toDayKey(job.scheduledDate);
  if (!start) return false;
  if (dayKeys.has(start)) return true;

  const end = toDayKey(job.scheduledEndDate);
  if (!end || end === start) return false;

  // Multi-day job: check each covered day against the window.
  const startMs = new Date(`${start}T00:00:00`).getTime();
  const endMs = new Date(`${end}T00:00:00`).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return false;
  for (let ms = startMs; ms <= endMs; ms += 24 * 60 * 60 * 1000) {
    if (dayKeys.has(format(new Date(ms), 'yyyy-MM-dd'))) return true;
  }
  return false;
}

const priorityColors: Record<string, string> = {
  critical: '#b91c1c',
  high: '#dc2626',
  medium: '#f59e0b',
  low: '#3b82f6',
  unknown: '#9ca3af',
};

export function PlannerMapView({
  jobs,
  onJobClick,
  teams = [],
  summariesByTeam,
  rangeStart,
  rangeDays,
}: PlannerMapViewProps) {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  // The set of day keys the range selector currently covers.
  const dayKeys = useMemo(() => {
    const keys = new Set<string>();
    for (let i = 0; i < rangeDays; i++) {
      keys.add(format(addDays(rangeStart, i), 'yyyy-MM-dd'));
    }
    return keys;
  }, [rangeStart, rangeDays]);

  // Scheduled jobs whose day(s) fall within the selected range.
  const jobsInRange = useMemo(() => jobs.filter((j) => isInDayWindow(j, dayKeys)), [jobs, dayKeys]);

  const jobsWithCoords = useMemo(
    () =>
      jobsInRange
        .map((j) => ({ job: j, coords: parseCoords(j.coordinates) }))
        .filter((j): j is { job: Job; coords: { lat: number; lng: number } } => j.coords !== null),
    [jobsInRange],
  );

  const bounds = useMemo(() => {
    if (jobsWithCoords.length === 0) return null;
    let minLat = 90;
    let maxLat = -90;
    let minLng = 180;
    let maxLng = -180;
    for (const { coords } of jobsWithCoords) {
      minLat = Math.min(minLat, coords.lat);
      maxLat = Math.max(maxLat, coords.lat);
      minLng = Math.min(minLng, coords.lng);
      maxLng = Math.max(maxLng, coords.lng);
    }
    return {
      latitude: (minLat + maxLat) / 2,
      longitude: (minLng + maxLng) / 2,
      zoom: jobsWithCoords.length === 1 ? 14 : 8,
    };
  }, [jobsWithCoords]);

  const jobsWithoutCoords = useMemo(
    () => jobsInRange.filter((j) => !parseCoords(j.coordinates)),
    [jobsInRange],
  );

  if (!MAPBOX_TOKEN) {
    return (
      <div className="flex-1 flex items-center justify-center bg-muted/30 rounded-lg border border-border mx-1">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Map unavailable — Mapbox token not configured
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden px-6 gap-3">
      <div className="flex-1 rounded-lg border border-border overflow-hidden relative">
        <Map
          mapboxAccessToken={MAPBOX_TOKEN}
          initialViewState={bounds ?? { latitude: 53.5, longitude: -1.5, zoom: 6 }}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
        >
          {jobsWithCoords.map(({ job, coords }) => (
            <Marker
              key={job.id}
              longitude={coords.lng}
              latitude={coords.lat}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                setSelectedJob(job);
              }}
            >
              <div
                className="w-6 h-6 rounded-full border-2 border-white shadow-lg cursor-pointer flex items-center justify-center"
                style={{ backgroundColor: priorityColors[job.priority] ?? '#E84A2B' }}
                title={job.title}
              >
                <span className="text-white text-[8px] font-bold">
                  {job.priority.charAt(0).toUpperCase()}
                </span>
              </div>
            </Marker>
          ))}

          {selectedJob &&
            (() => {
              const coords = parseCoords(selectedJob.coordinates);
              if (!coords) return null;
              return (
                <Popup
                  longitude={coords.lng}
                  latitude={coords.lat}
                  anchor="top"
                  onClose={() => setSelectedJob(null)}
                  closeButton
                  closeOnClick={false}
                  className="!max-w-[300px]"
                >
                  <div className="p-1">
                    <h4 className="font-semibold text-sm mb-1">{selectedJob.title}</h4>
                    {selectedJob.category && (
                      <p className="text-xs text-gray-500 mb-1">{selectedJob.category}</p>
                    )}
                    {selectedJob.location && (
                      <p className="text-xs text-gray-600 mb-1.5">{selectedJob.location}</p>
                    )}
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: priorityColors[selectedJob.priority] ?? '#999' }}
                      />
                      <span className="text-xs capitalize">{selectedJob.priority} priority</span>
                      {selectedJob.duration && (
                        <span className="text-xs text-gray-500">• {selectedJob.duration}h</span>
                      )}
                    </div>
                    {selectedJob.client && (
                      <p className="text-xs text-gray-500 mb-1.5">Client: {selectedJob.client}</p>
                    )}
                    <button
                      type="button"
                      onClick={() => onJobClick?.(selectedJob.id)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      View details →
                    </button>
                  </div>
                </Popup>
              );
            })()}
        </Map>

        <MapTeamUtilizationPanel teams={teams} summariesByTeam={summariesByTeam} />

        {/* Legend */}
        <div className="absolute top-3 right-3 bg-white dark:bg-zinc-900 border border-border rounded-lg p-3 shadow-md z-10">
          <p className="text-xs font-medium text-foreground mb-2">Priority</p>
          <div className="space-y-1">
            {Object.entries(priorityColors).map(([priority, color]) => (
              <div key={priority} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs capitalize text-foreground">{priority}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground">
              {jobsWithCoords.length} of {jobsInRange.length} jobs shown
            </p>
          </div>
        </div>
      </div>

      {/* Jobs without coordinates warning */}
      {jobsWithoutCoords.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-3 flex items-center gap-3">
          <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs text-amber-800 dark:text-amber-200">
              <span className="font-medium">
                {jobsWithoutCoords.length} scheduled job{jobsWithoutCoords.length > 1 ? 's' : ''}
              </span>{' '}
              not shown — missing coordinates. Update the site address to add them to the map.
            </p>
          </div>
          <div className="flex gap-1 flex-wrap">
            {jobsWithoutCoords.slice(0, 3).map((j) => (
              <Badge
                key={j.id}
                variant="outline"
                className="text-[10px] cursor-pointer"
                onClick={() => onJobClick?.(j.id)}
              >
                {j.title}
              </Badge>
            ))}
            {jobsWithoutCoords.length > 3 && (
              <Badge variant="outline" className="text-[10px]">
                +{jobsWithoutCoords.length - 3} more
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
